import { useEffect, useState } from 'react';
import { CloudOff, RefreshCw, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useOfflineSync } from './useOfflineSync';

/**
 * Bandeau global : hors ligne, synchronisation en cours, ou synchronisation terminée (5 s).
 * Affiché seulement pour un utilisateur connecté (la file d'attente est propre à chaque compte).
 */
export function OfflineStatusBanner({ enabled }: { enabled: boolean }) {
  const { t } = useTranslation();
  const { online, pending, failed, syncing, lastSyncAt, lastSyncedCount } = useOfflineSync();
  const [showSynced, setShowSynced] = useState(false);
  const [showFailed, setShowFailed] = useState(false);

  useEffect(() => {
    if (failed <= 0) return undefined;
    setShowFailed(true);
    const timer = window.setTimeout(() => setShowFailed(false), 10000);
    return () => window.clearTimeout(timer);
  }, [failed]);

  useEffect(() => {
    if (!lastSyncAt) return undefined;
    setShowSynced(true);
    const timer = window.setTimeout(() => setShowSynced(false), 5000);
    return () => window.clearTimeout(timer);
  }, [lastSyncAt]);

  if (!enabled) return null;

  let content: { icon: JSX.Element; text: string; tone: string } | null = null;
  if (!online) {
    content = {
      icon: <CloudOff className="h-4 w-4 shrink-0" aria-hidden />,
      text: pending > 0 ? t('offline.banner.offlinePending', { count: pending }) : t('offline.banner.offline'),
      tone: 'bg-amber-100 text-amber-900 dark:bg-amber-900/60 dark:text-amber-50',
    };
  } else if (syncing || pending > 0) {
    content = {
      icon: <RefreshCw className={`h-4 w-4 shrink-0 ${syncing ? 'animate-spin' : ''}`} aria-hidden />,
      text: t('offline.banner.syncing', { count: pending }),
      tone: 'bg-sky-100 text-sky-900 dark:bg-sky-900/60 dark:text-sky-50',
    };
  } else if (showFailed && failed > 0) {
    content = {
      icon: <CloudOff className="h-4 w-4 shrink-0" aria-hidden />,
      text: t('offline.banner.failed', { count: failed }),
      tone: 'bg-red-100 text-red-900 dark:bg-red-900/60 dark:text-red-50',
    };
  } else if (showSynced && lastSyncedCount > 0) {
    content = {
      icon: <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />,
      text: t('offline.banner.synced', { count: lastSyncedCount }),
      tone: 'bg-green-100 text-green-900 dark:bg-green-900/60 dark:text-green-50',
    };
  }
  if (!content) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      // Pastille au-dessus des barres de navigation (lecteur, mobile) ; ne bloque aucun clic.
      className={`pointer-events-none fixed bottom-20 left-1/2 z-[60] flex max-w-[min(92vw,40rem)] -translate-x-1/2 items-center gap-2 rounded-full px-4 py-2 text-xs font-medium shadow-lg sm:text-sm ${content.tone}`}
    >
      {content.icon}
      <span>{content.text}</span>
    </div>
  );
}
